import { registerArtifactPayload } from '@/features/documentPreview/artifactPayloadStore'
import { B2B_COPILOT_PLAN_DEMO_ARTIFACT_ID, B2B_COPILOT_PLAN_ZIP_ARTIFACT_ID } from '../demo/b2bCopilotPlanDemo.js'
import {
  B2B_COPILOT_PLAN_AI_LOG_STEPS_AFTER_WRITE,
  B2B_COPILOT_PLAN_AI_LOG_STEPS_INITIAL,
  B2B_COPILOT_PLAN_BEFORE_WRITE_MARKDOWN,
  B2B_COPILOT_PLAN_INTRO_MARKDOWN,
  B2B_COPILOT_PLAN_THOUGHT_DETAILS,
  buildB2bCopilotPlanArtifactPayload,
  buildB2bCopilotPlanFinalBlocks,
  buildB2bCopilotPlanZipArtifactPayload,
} from '../demo/b2bCopilotPlanDemoContent.js'
import { randomBetween } from '../streaming/computeStreamTypingDuration.js'
import { scaleChatStreamDelay } from '../streaming/chatStreamSpeed.js'
import { runTypewriter } from '../streaming/runTypewriter.js'
import { createDemoSelectionController } from '../demo/demoSelectionSwitch.js'
import { updatePendingB2bDemoMessage } from './patchB2bCopilotPlanDemoMessage.js'
import { runWorkflowStepsInStream } from './runWorkflowStepsInStream.js'

export function startB2bCopilotPlanDemoStreamLoop(ctx) {
  const { tid, pendingId, signal, session, setMessagesByThreadId } = ctx

  const state = {
    thoughtDetails: /** @type {string[]} */ ([]),
    blocks: /** @type {object[]} */ ([]),
    artifactId: B2B_COPILOT_PLAN_DEMO_ARTIFACT_ID,
  }

  const patch = (patchFields) => {
    updatePendingB2bDemoMessage(setMessagesByThreadId, tid, pendingId, patchFields)
  }

  const selection = createDemoSelectionController(state, patch)

  const schedule = (ms, fn) => {
    session.addTimeout(() => {
      if (signal.aborted) {
        session.dispose()
        return
      }
      fn()
    }, ms)
  }

  const runAfter = (ms, fn) => schedule(scaleChatStreamDelay(ms), fn)

  const runTypewriterText = (text, onUpdate, onDone) => {
    runTypewriter({
      session,
      signal,
      text,
      onUpdate,
      onDone,
    })
  }

  const runPreambleThinking = (onDone) => {
    patch({
      demoStreamActive: true,
      thoughtSummary: 'Thinking…',
      thoughtDetails: [],
      blocks: [],
    })
    selection.setInitial({
      agentId: 'research-specialist',
      personaId: 'founder',
    })
    runAfter(randomBetween(3000, 6000), onDone)
  }

  const runThoughtParagraphs = (onDone) => {
    let paraIdx = 0

    const runNextParagraph = () => {
      if (paraIdx >= B2B_COPILOT_PLAN_THOUGHT_DETAILS.length) {
        patch({ thoughtSummary: 'Thought for 7s', thoughtDetails: [...state.thoughtDetails] })
        runAfter(400, onDone)
        return
      }

      const fullText = B2B_COPILOT_PLAN_THOUGHT_DETAILS[paraIdx]
      state.thoughtDetails.push('')
      const currentParaIndex = state.thoughtDetails.length - 1
      patch({ thoughtDetails: [...state.thoughtDetails] })

      runTypewriterText(
        fullText,
        (partial) => {
          state.thoughtDetails[currentParaIndex] = partial
          patch({ thoughtDetails: [...state.thoughtDetails] })
        },
        () => {
          paraIdx++
          runAfter(300, runNextParagraph)
        },
      )
    }

    runNextParagraph()
  }

  const runMarkdownType = (content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'markdown', content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterText(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'markdown', content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runHeadingType = (level, content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'heading', level, content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterText(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'heading', level, content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  /**
   * @param {{ type: 'list', items: string[], ordered?: boolean }} block
   * @param {() => void} onDone
   */
  const runListType = (block, onDone) => {
    const blockIndex = state.blocks.length
    const items = block.items ?? []
    state.blocks.push({ type: 'list', items: [], ordered: block.ordered })
    patch({ blocks: [...state.blocks] })

    let itemIdx = 0

    const runNextItem = () => {
      if (itemIdx >= items.length) {
        onDone()
        return
      }

      const itemText = items[itemIdx]
      state.blocks[blockIndex].items.push('')
      const currentItemIndex = itemIdx
      patch({ blocks: [...state.blocks] })

      runTypewriterText(
        itemText,
        (partial) => {
          state.blocks[blockIndex].items[currentItemIndex] = partial
          patch({ blocks: [...state.blocks] })
        },
        () => {
          itemIdx++
          runAfter(200, runNextItem)
        },
      )
    }

    runNextItem()
  }

  /**
   * @param {{ type: 'table', data: unknown[] }} block
   * @param {() => void} onDone
   */
  const runTableReveal = (block, onDone) => {
    const blockIndex = state.blocks.length
    const rows = block.data ?? []
    state.blocks.push({ type: 'table', data: [] })
    patch({ blocks: [...state.blocks] })

    let rowIdx = 0

    const addNextRow = () => {
      if (rowIdx >= rows.length) {
        onDone()
        return
      }
      state.blocks[blockIndex].data.push(rows[rowIdx])
      patch({ blocks: [...state.blocks] })
      rowIdx++
      runAfter(280, addNextRow)
    }

    addNextRow()
  }

  /**
   * @param {object[]} templates
   * @param {() => void} onDone
   */
  const runWorkflowSteps = (templates, onDone) => {
    const logIndex = state.blocks.length
    state.blocks.push({ type: 'aiResponseLog', status: 'ok', showLogStatus: false, steps: [] })

    const patchLog = (steps) => {
      state.blocks[logIndex] = {
        type: 'aiResponseLog',
        status: 'ok',
        showLogStatus: false,
        steps,
      }
      patch({ blocks: [...state.blocks] })
    }

    runWorkflowStepsInStream({
      templates,
      session,
      signal,
      runAfter: schedule,
      onStepsChange: patchLog,
      onDone,
    })
  }

  const runInstantFileArtifacts = (onDone) => {
    registerArtifactPayload({
      artifactId: state.artifactId,
      kind: 'doc',
      fileName: 'B2B Copilot — Development Plan.md',
      description: 'Markdown',
      payload: buildB2bCopilotPlanArtifactPayload(),
    })
    registerArtifactPayload({
      artifactId: B2B_COPILOT_PLAN_ZIP_ARTIFACT_ID,
      kind: 'doc',
      fileName: 'B2B Copilot.zip',
      description: 'Zip archive',
      payload: buildB2bCopilotPlanZipArtifactPayload(),
    })
    state.blocks.push(
      {
        type: 'fileArtifact',
        fileName: 'B2B Copilot — Development Plan.md',
        description: 'Markdown',
        artifactKind: 'doc',
        artifactId: state.artifactId,
      },
      {
        type: 'fileArtifact',
        fileName: 'B2B Copilot.zip',
        description: 'Zip archive',
        artifactKind: 'doc',
        artifactId: B2B_COPILOT_PLAN_ZIP_ARTIFACT_ID,
      },
    )
    patch({ blocks: [...state.blocks] })
    runAfter(300, onDone)
  }

  const runTailBlocks = () => {
    const tail = buildB2bCopilotPlanFinalBlocks(state.artifactId, B2B_COPILOT_PLAN_ZIP_ARTIFACT_ID).slice(7)

    let i = 0

    const addNext = () => {
      if (i >= tail.length) {
        patch({
          demoStreamActive: false,
          thoughtSummary: 'Thought for 7s',
          blocks: [...state.blocks],
        })
        session.dispose()
        return
      }

      const block = tail[i]

      if (block.type === 'markdown' && typeof block.content === 'string') {
        const blockIndex = state.blocks.length
        state.blocks.push({ type: 'markdown', content: '', variant: block.variant })
        patch({ blocks: [...state.blocks] })
        runTypewriterText(
          block.content,
          (partial) => {
            state.blocks[blockIndex] = { type: 'markdown', content: partial, variant: block.variant }
            patch({ blocks: [...state.blocks] })
          },
          () => {
            i++
            runAfter(200, addNext)
          },
        )
        return
      }

      if (block.type === 'heading' && typeof block.content === 'string') {
        runHeadingType(block.level, block.content, () => {
          i++
          runAfter(200, addNext)
        })
        return
      }

      if (block.type === 'list' && Array.isArray(block.items)) {
        runListType(block, () => {
          i++
          runAfter(200, addNext)
        })
        return
      }

      if (block.type === 'table' && Array.isArray(block.data)) {
        runTableReveal(block, () => {
          i++
          runAfter(200, addNext)
        })
        return
      }

      state.blocks.push(block)
      patch({ blocks: [...state.blocks] })
      i++
      runAfter(200, addNext)
    }

    addNext()
  }

  runPreambleThinking(() => {
    runThoughtParagraphs(() => {
      runMarkdownType(B2B_COPILOT_PLAN_INTRO_MARKDOWN, () => {
        runWorkflowSteps(B2B_COPILOT_PLAN_AI_LOG_STEPS_INITIAL, () => {
          runMarkdownType(B2B_COPILOT_PLAN_BEFORE_WRITE_MARKDOWN, () => {
            selection.switchTo({ agentId: 'financial-expert' })
            runWorkflowSteps(B2B_COPILOT_PLAN_AI_LOG_STEPS_AFTER_WRITE, () => {
              runInstantFileArtifacts(() => {
                runTailBlocks()
              })
            })
          })
        })
      })
    })
  })
}
